import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, Send, Loader2 } from 'lucide-react';
import { API, adminHeaders, adminLogout, getAdminToken } from '../../api/client';
import { logger } from '../../lib/logger';

export default function AdminAI() {
  const nav = useNavigate();
  const [status, setStatus] = useState(null);
  const [batchSize, setBatchSize] = useState(20);
  const [regenSlug, setRegenSlug] = useState('');
  const [aiBusy, setAiBusy] = useState(false);
  const [aiLog, setAiLog] = useState('');

  const [digestSubject, setDigestSubject] = useState("AI Framework — this week's picks");
  const [digestIntro, setDigestIntro] = useState('');
  const [digestSlugsRaw, setDigestSlugsRaw] = useState('');
  const [digestTest, setDigestTest] = useState('');
  const [digestBusy, setDigestBusy] = useState(false);
  const [digestLog, setDigestLog] = useState('');
  const [sends, setSends] = useState([]);

  const refreshStatus = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/ai/summaries/status`, { headers: adminHeaders() });
      setStatus(data);
    } catch (e) {
      if (e?.response?.status === 401) {
        await adminLogout();
        nav('/admin/login');
      }
    }
  };

  const refreshSends = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/newsletter/sends`, { headers: adminHeaders(), params: { limit: 10 } });
      setSends(data.items || []);
    } catch (e) {
      logger.error('AdminAI: failed to load newsletter sends history', e);
    }
  };

  useEffect(() => {
    if (!getAdminToken()) {
      nav('/admin/login');
      return;
    }
    refreshStatus();
    refreshSends();
  }, [nav]);

  const runBackfill = async () => {
    setAiBusy(true);
    setAiLog('Running…');
    try {
      const { data } = await axios.post(
        `${API}/admin/ai/summaries/backfill`,
        { limit: Number(batchSize) || 20, dry_run: false },
        { headers: adminHeaders() }
      );
      setAiLog(JSON.stringify(data, null, 2));
      await refreshStatus();
    } catch (e) {
      setAiLog('ERROR: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setAiBusy(false);
    }
  };

  const runRegenOne = async () => {
    const slug = regenSlug.trim();
    if (!slug) return;
    setAiBusy(true);
    setAiLog(`Regenerating ${slug}…`);
    try {
      const { data } = await axios.post(
        `${API}/admin/ai/summaries/${slug}`,
        {},
        { headers: adminHeaders() }
      );
      setAiLog(JSON.stringify(data, null, 2));
      await refreshStatus();
    } catch (e) {
      setAiLog('ERROR: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setAiBusy(false);
    }
  };

  const sendDigest = async () => {
    setDigestBusy(true);
    setDigestLog('Sending…');
    try {
      const slugs = digestSlugsRaw
        .split(/[\s,]+/)
        .map((s) => s.trim())
        .filter(Boolean);
      const body = {
        subject: digestSubject.trim() || undefined,
        intro: digestIntro.trim() || undefined,
        slugs: slugs.length ? slugs : null,
        test_email: digestTest.trim() || null,
      };
      const { data } = await axios.post(
        `${API}/admin/newsletter/send-digest`,
        body,
        { headers: adminHeaders() }
      );
      setDigestLog(JSON.stringify(data, null, 2));
      await refreshSends();
    } catch (e) {
      setDigestLog('ERROR: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setDigestBusy(false);
    }
  };

  return (
    <div className="page-container admin-page" data-testid="admin-ai-page">
      <div className="admin-topbar">
        <h1 className="page-title">AI & Newsletter</h1>
        <div className="admin-topbar-actions">
          <Link to="/admin/analytics" className="pill-btn">Analytics</Link>
          <Link to="/admin/tools" className="pill-btn">Tools</Link>
          <button
            className="pill-btn ghost"
            onClick={async () => { await adminLogout(); nav('/admin/login'); }}
            type="button"
          >Log out</button>
        </div>
      </div>

      <h2 style={{ marginTop: 8 }}><Sparkles size={14} style={{ verticalAlign: '-2px' }} /> AI Summaries</h2>
      <p className="muted" style={{ marginBottom: 8 }}>
        Auto-generate a factual 2-paragraph summary for every tool using Claude Sonnet 4.5 (Emergent Universal Key).
        Safe to run repeatedly — it only touches tools missing a summary.
      </p>

      <div className="admin-ai-grid" data-testid="ai-stats">
        <div className="admin-stat-card">
          <div className="stat-value" data-testid="ai-stat-total">{status?.total_active?.toLocaleString() ?? '—'}</div>
          <div className="stat-label">Active tools</div>
        </div>
        <div className="admin-stat-card">
          <div className="stat-value" data-testid="ai-stat-covered">{status?.covered?.toLocaleString() ?? '—'}</div>
          <div className="stat-label">With AI summary</div>
        </div>
        <div className="admin-stat-card">
          <div className="stat-value" data-testid="ai-stat-missing">{status?.missing_summary?.toLocaleString() ?? '—'}</div>
          <div className="stat-label">Missing</div>
        </div>
      </div>

      <div className="admin-action-card">
        <h3>Batch backfill</h3>
        <p>Fills the next N tools that are missing a summary. Recommended batch size: 20 (about ~2 minutes on Sonnet).</p>
        <div className="admin-action-row">
          <input
            type="number"
            min={1}
            max={100}
            value={batchSize}
            onChange={(e) => setBatchSize(e.target.value)}
            data-testid="ai-batch-size"
          />
          <button
            className="pill-btn primary"
            onClick={runBackfill}
            disabled={aiBusy}
            data-testid="ai-run-backfill"
          >
            {aiBusy ? <><Loader2 size={12} className="spin" /> Working…</> : <>Run backfill</>}
          </button>
        </div>
      </div>

      <div className="admin-action-card">
        <h3>Regenerate one</h3>
        <p>Force-regenerate the summary for a single tool (by slug), useful when the description has been rewritten.</p>
        <div className="admin-action-row">
          <input
            type="text"
            placeholder="tool-slug"
            value={regenSlug}
            onChange={(e) => setRegenSlug(e.target.value)}
            data-testid="ai-regen-slug"
          />
          <button
            className="pill-btn"
            onClick={runRegenOne}
            disabled={aiBusy || !regenSlug.trim()}
            data-testid="ai-run-regen"
          >
            Regenerate
          </button>
        </div>
      </div>

      {aiLog && <pre className="admin-log" data-testid="ai-log">{aiLog}</pre>}

      <h2 style={{ marginTop: 40 }}><Send size={14} style={{ verticalAlign: '-2px' }} /> Newsletter Digest</h2>
      <p className="muted" style={{ marginBottom: 8 }}>
        Send a curated digest email to every confirmed newsletter subscriber via Resend.
        Leave the tool slugs blank to auto-pick the 8 highest-scoring tools added in the last 14 days.
      </p>

      <div className="admin-action-card">
        <h3>Compose &amp; send</h3>
        <div className="admin-action-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 10 }}>
          <input
            type="text"
            value={digestSubject}
            onChange={(e) => setDigestSubject(e.target.value)}
            placeholder="Subject"
            data-testid="digest-subject"
            style={{ minWidth: '100%' }}
          />
          <input
            type="text"
            value={digestIntro}
            onChange={(e) => setDigestIntro(e.target.value)}
            placeholder="Intro line (optional)"
            data-testid="digest-intro"
            style={{ minWidth: '100%' }}
          />
          <input
            type="text"
            value={digestSlugsRaw}
            onChange={(e) => setDigestSlugsRaw(e.target.value)}
            placeholder="Comma or space-separated tool slugs (optional)"
            data-testid="digest-slugs"
            style={{ minWidth: '100%' }}
          />
          <div className="admin-action-row" style={{ marginTop: 6 }}>
            <input
              type="email"
              value={digestTest}
              onChange={(e) => setDigestTest(e.target.value)}
              placeholder="Send preview only to (optional)"
              data-testid="digest-test-email"
              style={{ minWidth: 260 }}
            />
            <button
              className="pill-btn primary"
              onClick={sendDigest}
              disabled={digestBusy}
              data-testid="digest-send"
            >
              {digestBusy && <><Loader2 size={12} className="spin" /> Sending…</>}
              {!digestBusy && digestTest.trim() && 'Send preview'}
              {!digestBusy && !digestTest.trim() && 'Send to all subscribers'}
            </button>
          </div>
        </div>
      </div>

      {digestLog && <pre className="admin-log" data-testid="digest-log">{digestLog}</pre>}

      {sends.length > 0 && (
        <div className="admin-action-card" style={{ marginTop: 20 }}>
          <h3>Recent sends</h3>
          <ul className="rank-list" data-testid="digest-history">
            {sends.map((s) => (
              <li key={s.id}>
                <span>{s.subject} — {s.recipient_count} recipients, {s.sent}/{s.recipient_count} delivered</span>
                <span className="rank-count">{new Date(s.created_at).toLocaleDateString()}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
